import React, { useState, useRef } from 'react';
import {
  Shield,
  Search,
  FileText,
  MessageSquare,
  Award,
  RefreshCw,
  CheckCircle,
  TrendingUp,
  Scale,
  Flag,
  Package,
  Globe,
  Laptop,
  ShoppingCart,
  UtensilsCrossed,
  Shirt,
  Heart,
  Briefcase,
  Film,
  Factory,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Users,
  Clock,
  Target,
  Zap,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import {
  faqs,
  industries,
  services,
  benefits,
  trustSignals,
  pricingPlans
} from '../mockData';

const iconMap = {
  Search,
  FileText,
  MessageSquare,
  Shield,
  Award,
  RefreshCw,
  CheckCircle,
  TrendingUp,
  Scale,
  Flag,
  Package,
  Globe,
  Laptop,
  ShoppingCart,
  UtensilsCrossed,
  Shirt,
  Heart,
  Briefcase,
  Film,
  Factory
};

export const Home = ({ onOpenPopup }) => {
  const pricingRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % pricingPlans.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + pricingPlans.length) % pricingPlans.length);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-20 bg-gradient-to-br from-blue-50 via-white to-amber-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-blue-900 text-white px-4 py-2 text-sm">
              USPTO Filing Experts • 1000+ Trademarks Filed
            </Badge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-blue-900 mb-6 leading-tight">
              Protect Your Brand with Expert Trademark Registration
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
              Secure your brand identity with professional trademark registration services. Navigate the USPTO process with confidence and establish exclusive rights to your brand nationwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={onOpenPopup}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-8 py-6 font-bold shadow-lg hover:shadow-xl transition-all"
              >
                Register My Trademark
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                variant="outline"
                className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white text-lg px-8 py-6 font-bold transition-all"
              >
                View Pricing
              </Button>
            </div>
            <p className="mt-6 text-sm text-gray-600">
              ⚡ <strong>Limited Time Offer:</strong> Get started from just $45 + Federal Filing Fee
            </p>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-blue-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {trustSignals.map((signal, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-amber-400 mb-2">{signal.number}</div>
                <div className="text-white text-sm md:text-base">{signal.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Company */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">About Trademark Name Registry</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            </div>
            <Card className="border-2 border-blue-100 shadow-lg">
              <CardContent className="p-8 md:p-12">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  At Trademark Name Registry, we are dedicated to helping businesses and entrepreneurs protect their most valuable asset – their brand. With over a decade of experience in trademark law and USPTO procedures, we have successfully filed and secured more than 1,000 trademarks for clients across the United States.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Our team of trademark specialists understands that your brand represents years of hard work, innovation, and reputation building. We are committed to providing comprehensive trademark registration services that give you peace of mind and legal protection against infringement.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  From initial trademark searches to final registration and ongoing monitoring, we guide you through every step of the process. Our mission is to make trademark registration accessible, affordable, and stress-free, ensuring your brand receives the protection it deserves in today's competitive marketplace.
                </p>
                <div className="grid md:grid-cols-3 gap-6 mt-8">
                  <div className="text-center p-6 bg-blue-50 rounded-lg">
                    <Users className="w-10 h-10 text-blue-900 mx-auto mb-3" />
                    <h4 className="font-bold text-blue-900 mb-2">Expert Team</h4>
                    <p className="text-sm text-gray-600">Experienced trademark attorneys and specialists</p>
                  </div>
                  <div className="text-center p-6 bg-blue-50 rounded-lg">
                    <Clock className="w-10 h-10 text-blue-900 mx-auto mb-3" />
                    <h4 className="font-bold text-blue-900 mb-2">Fast Processing</h4>
                    <p className="text-sm text-gray-600">Quick turnaround on all trademark filings</p>
                  </div>
                  <div className="text-center p-6 bg-blue-50 rounded-lg">
                    <Target className="w-10 h-10 text-blue-900 mx-auto mb-3" />
                    <h4 className="font-bold text-blue-900 mb-2">95% Success Rate</h4>
                    <p className="text-sm text-gray-600">Proven track record of approvals</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trademark Registration Overview */}
      <section id="overview" className="py-20 bg-gradient-to-br from-blue-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">Understanding Trademark Registration</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-700">
              A comprehensive guide to protecting your brand identity through federal trademark registration
            </p>
          </div>
          <Card className="border-2 border-blue-100 shadow-lg">
            <CardContent className="p-8 md:p-12">
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">What is a Trademark?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    A trademark is any word, phrase, symbol, design, or combination thereof that identifies and distinguishes the source of goods or services from those of others. It's the cornerstone of your brand identity and serves as a direct connection between your business and your customers. Trademarks can include brand names, logos, slogans, product names, and even unique packaging designs that set your offerings apart in the marketplace.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">Why Federal Registration Matters</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    While common law rights can be established through use, federal trademark registration with the United States Patent and Trademark Office (USPTO) provides significantly stronger legal protection and numerous benefits that common law rights cannot match. Federal registration creates a public record of your ownership, provides nationwide priority, and allows you to use the ® symbol – a powerful deterrent to potential infringers.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Federal registration also gives you the legal presumption of ownership and the exclusive right to use the mark nationwide on or in connection with the goods and services listed in the registration. This is particularly crucial in today's digital economy where businesses operate across state lines and online marketplaces require proof of trademark rights to remove counterfeit products.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">The Registration Process</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    The USPTO trademark registration process is complex and requires careful attention to detail. It begins with a comprehensive trademark search to identify potential conflicts, followed by preparation and filing of the application with proper identification of goods/services and classification. The USPTO examining attorney then reviews the application, which can take several months.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    If issues arise, the examining attorney will issue an office action requiring a response. Once approved, the mark is published for opposition, allowing third parties to challenge the registration. If no opposition is filed (or if opposition is overcome), the mark proceeds to final registration. Throughout this 8-12 month journey, having experienced professionals on your side significantly increases your chances of success and helps navigate any challenges that arise.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Detailed Trademark Services */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">Comprehensive Trademark Services</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              End-to-end trademark registration and protection services tailored to your business needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={index} className="border-2 border-blue-100 hover:border-amber-500 hover:shadow-xl transition-all group">
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-800 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-7 h-7 text-amber-400" />
                    </div>
                    <CardTitle className="text-xl text-blue-900">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-choose-us" className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Trademark Name Registry</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto">
              Trusted by hundreds of businesses nationwide for expert trademark registration services
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all">
              <CardContent className="p-6 text-center">
                <Shield className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Expert Guidance</h3>
                <p className="text-blue-100">
                  Experienced trademark specialists with deep knowledge of USPTO procedures and trademark law
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">95% Success Rate</h3>
                <p className="text-blue-100">
                  Proven track record of successful trademark registrations across all industries
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all">
              <CardContent className="p-6 text-center">
                <Zap className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Fast Turnaround</h3>
                <p className="text-blue-100">
                  Quick processing and filing of your trademark application to secure your priority date
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all">
              <CardContent className="p-6 text-center">
                <Users className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Dedicated Support</h3>
                <p className="text-blue-100">
                  Personal case manager and ongoing support throughout the entire registration process
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section id="process" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">Our Simple 3-Step Process</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Get your trademark registered in three easy steps
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-2 border-blue-100 hover:border-amber-500 hover:shadow-xl transition-all relative">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  1
                </div>
                <CardContent className="p-8 pt-12 text-center">
                  <Search className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">Search & Analyze</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We conduct a comprehensive trademark search to identify potential conflicts and assess the registrability of your mark. Our detailed analysis helps you understand any risks and provides strategic recommendations.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-100 hover:border-amber-500 hover:shadow-xl transition-all relative">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  2
                </div>
                <CardContent className="p-8 pt-12 text-center">
                  <FileText className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">Prepare & File</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Our experts prepare your trademark application with precision, ensuring proper classification, accurate descriptions, and compliance with USPTO requirements. We file electronically to secure your priority date immediately.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-100 hover:border-amber-500 hover:shadow-xl transition-all relative">
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  3
                </div>
                <CardContent className="p-8 pt-12 text-center">
                  <Award className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">Monitor & Secure</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We handle all USPTO communications, respond to office actions if needed, and monitor your application through publication and registration. Once approved, we help maintain your trademark rights for long-term protection.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section id="pricing" ref={pricingRef} className="py-20 bg-gradient-to-br from-blue-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">Transparent Pricing Plans</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the perfect package for your trademark registration needs
            </p>
          </div>

          {/* Desktop Grid - Only show on large screens */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card
                key={index}
                className={`border-2 ${
                  plan.popular ? 'border-amber-500 shadow-2xl scale-105' : 'border-blue-100'
                } hover:shadow-xl transition-all relative`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-1">
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl text-blue-900 mb-2">{plan.name}</CardTitle>
                  <div className="text-4xl font-bold text-blue-900 mb-2">
                    {plan.price}
                    <span className="text-base font-normal text-gray-600"> + Federal Filing Fee</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 mb-6 max-h-80 overflow-y-auto">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mb-4 italic">({plan.note})</p>
                  <Button
                    onClick={onOpenPopup}
                    className={`w-full ${
                      plan.popular
                        ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                        : 'bg-blue-900 hover:bg-blue-800'
                    } text-white font-semibold py-6`}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile/Tablet Slider - Show below lg breakpoint */}
          <div className="lg:hidden relative px-12">
            <div className="max-w-md mx-auto">
              <Card
                className={`border-2 ${
                  pricingPlans[currentSlide].popular ? 'border-amber-500 shadow-2xl' : 'border-blue-100'
                } relative`}
              >
                {pricingPlans[currentSlide].badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-1">
                      {pricingPlans[currentSlide].badge}
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl text-blue-900 mb-2">{pricingPlans[currentSlide].name}</CardTitle>
                  <div className="text-4xl font-bold text-blue-900 mb-2">
                    {pricingPlans[currentSlide].price}
                    <span className="text-base font-normal text-gray-600"> + Federal Filing Fee</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 mb-6 max-h-80 overflow-y-auto">
                    {pricingPlans[currentSlide].features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mb-4 italic">({pricingPlans[currentSlide].note})</p>
                  <Button
                    onClick={onOpenPopup}
                    className={`w-full ${
                      pricingPlans[currentSlide].popular
                        ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                        : 'bg-blue-900 hover:bg-blue-800'
                    } text-white font-semibold py-6`}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                aria-label="Previous plan"
              >
                <ChevronLeft className="w-6 h-6 text-blue-900" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                aria-label="Next plan"
              >
                <ChevronRight className="w-6 h-6 text-blue-900" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {pricingPlans.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentSlide === index ? 'bg-amber-500 w-8' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to plan ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section id="industries" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">Industries We Serve</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Providing expert trademark registration services across all business sectors
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => {
              const IconComponent = iconMap[industry.icon];
              return (
                <Card key={index} className="border-2 border-blue-100 hover:border-amber-500 hover:shadow-lg transition-all text-center group">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="w-8 h-8 text-amber-400" />
                    </div>
                    <h3 className="text-lg font-bold text-blue-900 mb-2">{industry.name}</h3>
                    <p className="text-sm text-gray-600">{industry.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="py-20 bg-gradient-to-br from-blue-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">Benefits of Trademark Registration</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover the powerful advantages of federal trademark protection
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <Card key={index} className="border-2 border-blue-100 hover:border-amber-500 hover:shadow-xl transition-all">
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-800 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-amber-400" />
                    </div>
                    <CardTitle className="text-xl text-blue-900">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">Frequently Asked Questions</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Get answers to common questions about trademark registration
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-2 border-blue-100 rounded-lg px-6 hover:border-amber-500 transition-colors"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-blue-900 hover:text-amber-600 py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Strong CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Protect Your Brand?
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Don't wait until it's too late. Secure your trademark today and safeguard your business's most valuable asset. Our expert team is ready to guide you through every step of the registration process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <Button
                onClick={onOpenPopup}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-10 py-7 font-bold shadow-xl hover:shadow-2xl transition-all"
              >
                Protect My Brand Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <p className="text-amber-400 font-semibold text-lg">
              ⚡ Limited Time: Starting at just $45 + Federal Filing Fee
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">Contact Us</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Have questions? Our trademark experts are here to help
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-blue-100 hover:border-amber-500 transition-all text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">Call Us</h3>
                  <a href="tel:+11231234567" className="text-gray-600 hover:text-amber-600 transition-colors">
                    +1 (123) 123-4567
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-100 hover:border-amber-500 transition-all text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">Email Us</h3>
                  <a
                    href="mailto:info@trademarknameregistry.com"
                    className="text-gray-600 hover:text-amber-600 transition-colors break-all text-sm"
                  >
                    info@trademarknameregistry.com
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-100 hover:border-amber-500 transition-all text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">Visit Us</h3>
                  <p className="text-gray-600 text-sm">
                    1150 S Olive St 10th Floor<br />
                    Los Angeles, CA 90015
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-8 border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-amber-50">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-blue-900 mb-4">Get Your Free Trademark Consultation</h3>
                <p className="text-gray-700 mb-6">
                  Click below to schedule a consultation with our trademark experts and learn how we can protect your brand
                </p>
                <Button
                  onClick={onOpenPopup}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-10 py-6 font-bold"
                >
                  Get Started Today
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
